// [APP-FORK] 앱(WebView) 점주 검증 QR 통합 진입 페이지.
// 네이티브가 naegift://{type}/{uuid} 스캔 → 웹뷰로 /app/qr/{type}/{uuid} 로드 →
// 이 페이지가 역매핑 EP(@Public)를 호출해 기존 검증 라우트로 replace navigate 한다.
//   verify: GET {API_SERVER_PUBLIC}app/qr/verify/{uuid}
//           → {resultCode,...,data:{storeNo,userGiftNo,giftNo}} (전역 인터셉터 래핑 → res.data.data 언랩)
//           → /mobile/verifyconfirm/{storeNo}/{userGiftNo}/{giftNo}
//             (기존 VerifyConfirm 이 검증 EP 호출 + 성공/실패/403/409/로그인 전부 처리 = 설계 A)
//   coupon(docs/promotion-ledger.html §15): GET {API_SERVER_PUBLIC}app/qr/coupon/{uuid}
//           → {resultCode,...,data:{promotionCouponNo,userNo,promotionNo}}
//           → /mobile/couponverifyconfirm/{promotionCouponNo}
//             (CouponVerifyConfirm 이 검증 EP 호출 + 성공/실패 처리 — 코드는 훨씬 단순, 403/409 없음)
// ★store/gift 는 user-app 소관 — 이 admin 페이지는 verify/coupon 만 담당.
// 실패(404/네트워크/미지원 type)는 기존 /mobile/verifyinvalid 로 위임(로그인/storeNo 처리 재사용).
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';

import LazySpinner from '../admin/LazySpinner';

export default function AppQrEntry() {
  const navigate = useNavigate();
  const { type, uuid } = useParams();
  const location = useLocation();
  const { fetchData } = useAxiosAdminFetch();
  // StrictMode 이중 실행 가드 + 단일 처리 보장
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const publicBase = process.env.REACT_APP_API_SERVER_PUBLIC;

    const resolve = async () => {
      // 이 admin 진입 페이지는 verify/coupon 만 담당(store/gift 는 user-app 소관)
      if (!uuid || (type !== 'verify' && type !== 'coupon')) {
        navigate('/mobile/verifyinvalid', { replace: true });
        return;
      }

      try {
        if (type === 'coupon') {
          const res = await axios.get(`${publicBase}app/qr/coupon/${uuid}`, {
            withCredentials: true,
          });
          const { promotionCouponNo, issuerStoreNo } = res?.data?.data ?? {};
          if (promotionCouponNo == null) {
            navigate('/mobile/verifyinvalid', { replace: true });
            return;
          }
          // navigate(`/mobile/couponverifyconfirm/${promotionCouponNo}`, { replace: true });
          // [2026-11-04] 기프트 검증처럼 주소에 상점번호를 넣는다.
          //  상점 발행 쿠폰 → 발행 상점(다른 상점 계정이면 CouponVerifyConfirm 이 "상점 불일치")
          //  운영자·채널 쿠폰 → 로그인한 상점관리자의 상점(여러 상점에서 쓰는 쿠폰)
          //  미로그인이면 fetchData 가 상점 공통 로그인으로 보내고 로그인 후 이 주소로 돌아온다.
          let storeNo = issuerStoreNo ?? null;
          if (storeNo == null) {
            const p = await fetchData({
              method: 'GET',
              url: `${process.env.REACT_APP_API_SERVER}profile`,
            });
            if (p?.status === 401) return; // 로그인 화면으로 이동됨
            storeNo = p?.data?.data?.storeNo ?? null;
          }
          if (storeNo == null) {
            navigate(`/mobile/login?redirect=${encodeURIComponent(location.pathname)}`, {
              replace: true,
            });
            return;
          }
          navigate(`/mobile/couponverifyconfirm/${storeNo}/${promotionCouponNo}`, {
            replace: true,
          });
          return;
        }

        const res = await axios.get(`${publicBase}app/qr/verify/${uuid}`, {
          withCredentials: true,
        });
        // 전역 인터셉터 래핑: {resultCode,...,data:{...}} → data 언랩
        const { storeNo, userGiftNo, giftNo } = res?.data?.data ?? {};
        if (
          storeNo == null ||
          userGiftNo == null ||
          giftNo == null
        ) {
          navigate('/mobile/verifyinvalid', { replace: true });
          return;
        }
        // 기존 검증 라우트로 위임(검증 실행·결과화면·인증은 VerifyConfirm 담당)
        navigate(
          `/mobile/verifyconfirm/${storeNo}/${userGiftNo}/${giftNo}`,
          { replace: true }
        );
      } catch (error) {
        // 404(만료/오매핑) 등 → 기존 invalid 처리로 위임
        console.error('[app-qr] resolve failed:', error);
        navigate('/mobile/verifyinvalid', { replace: true });
      }
    };

    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, uuid, navigate]);

  return <LazySpinner />;
}
